import { Job, SupabaseJob } from '@applicator/shared'
import { useEffect } from 'react'

import { loadRelevanceSettings } from '../../lib/relevanceSettings'
import { supabase } from '../../lib/supabase'

import { UserRules } from './types'
import { mapSupabaseToJob } from './utils'

interface EffectsParams {
  searchQueryId: string | null | undefined
  loadJobs: () => Promise<void>
  onRefreshRef?: ((fn: () => void) => void) | undefined
  onAddJobRef?: ((fn: () => void) => void) | undefined
  onBulkDeleteRef?: ((fn: () => void) => void) | undefined
  onBulkAddToJobsRef?: ((fn: () => void) => void) | undefined
  onSelectedCountChange?: ((count: number) => void) | undefined
  onStatusCountsChange: (counts: Record<string, number>) => void
  jobs: Job[]
  supabaseJobs: SupabaseJob[]
  userRules: UserRules
  selectedRows: Set<number>
  handleAddJob: () => Promise<void>
  handleBulkDelete: () => void
  handleBulkAddToJobs: () => Promise<void>
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>
  setUserRules: React.Dispatch<React.SetStateAction<UserRules>>
  setRelevanceSettings: React.Dispatch<React.SetStateAction<Awaited<ReturnType<typeof loadRelevanceSettings>> | null>>
}

export const useJobsTableEffects = ({
  searchQueryId,
  loadJobs,
  onRefreshRef,
  onAddJobRef,
  onBulkDeleteRef,
  onBulkAddToJobsRef,
  onSelectedCountChange,
  onStatusCountsChange,
  jobs,
  supabaseJobs,
  userRules,
  selectedRows,
  handleAddJob,
  handleBulkDelete,
  handleBulkAddToJobs,
  setJobs,
  setSupabaseJobs,
  setUserRules,
  setRelevanceSettings,
}: EffectsParams) => {
  // Load jobs on mount and when searchQueryId changes
  useEffect(() => {
    loadJobs()
  }, [searchQueryId, loadJobs])

  // Load relevance settings
  useEffect(() => {
    const loadSettings = async () => {
      const settings = await loadRelevanceSettings()
      setRelevanceSettings(settings)
    }
    loadSettings()
  }, [setRelevanceSettings])

  // Load user date rules from Supabase
  useEffect(() => {
    const loadUserRules = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return

        const { data, error: fetchError } = await supabase
          .from('user_settings')
          .select('follow_up_days_after_applied, deadline_days_after_saved')
          .eq('user_id', user.id)
          .maybeSingle()

        if (fetchError) {
          // Ignore errors - use defaults if settings don't exist
          console.warn('Failed to load user date rules (using defaults):', fetchError)
          return
        }

        if (data) {
          setUserRules({
            follow_up_days_after_applied: data.follow_up_days_after_applied ?? 7,
            deadline_days_after_saved: data.deadline_days_after_saved ?? 14,
          })
        }
      } catch (err) {
        console.error('Error loading user date rules:', err)
      }
    }

    loadUserRules()
  }, [setUserRules])

  // Recalculate jobs when userRules change (to update auto-calculated dates)
  useEffect(() => {
    if (supabaseJobs.length > 0 && jobs.length > 0) {
      // Only recalculate if we have both supabaseJobs and jobs already loaded
      // This ensures dates are recalculated with new rules after initial load
      const recalculatedJobs = supabaseJobs.map((sbJob) => mapSupabaseToJob(sbJob, userRules))
      setJobs(recalculatedJobs)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRules]) // Recalculate when userRules change

  // Realtime subscription for relevance updates
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null
    let isMounted = true

    const setupSubscription = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user || !isMounted) return

        channel = supabase
          .channel('jobs-relevance-updates-table')
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'jobs',
              filter: `user_id=eq.${user.id}`
            },
            (payload: { new: SupabaseJob; old: SupabaseJob }) => {
              if (!isMounted) return
              console.log('[JOBS TABLE] 🔄 Update received:', payload.new)
              
              const updatedJob = payload.new as SupabaseJob
              
              // Update supabaseJobs with new data
              setSupabaseJobs((prev) => {
                const updated = prev.map(job => 
                  job.id === updatedJob.id 
                    ? { ...job, ...updatedJob } 
                    : job
                )
                // If job not in list, add it
                if (!prev.find(j => j.id === updatedJob.id)) {
                  return [...prev, updatedJob]
                }
                return updated
              })
              
              // Update jobs state with formatted dates
              setJobs((prev) => {
                const jobIndex = prev.findIndex(j => j.id === updatedJob.id)
                if (jobIndex >= 0) {
                  const updated = [...prev]
                  const mappedJob = mapSupabaseToJob(updatedJob, userRules)
                  updated[jobIndex] = { ...updated[jobIndex], ...mappedJob }
                  return updated
                }
                return prev
              })
            }
          )
          .subscribe()

        return channel
      } catch (error) {
        console.error('[JOBS TABLE] ❌ Error setting up realtime subscription:', error)
        return null
      }
    }

    const cleanupPromise = setupSubscription()
    
    return () => {
      isMounted = false
      cleanupPromise.then((ch) => {
        if (ch) {
          // Use unsubscribe first to gracefully close the channel
          try {
            ch.unsubscribe()
          } catch (error) {
            // Ignore errors during cleanup
          }
          // Then remove channel from Supabase client
          try {
            supabase.removeChannel(ch)
          } catch (error) {
            // Ignore WebSocket errors during cleanup - they're harmless
          }
        }
      }).catch(() => {
        // Ignore cleanup errors
      })
    }
  }, [userRules, setJobs, setSupabaseJobs])

  // Expose refresh function to parent
  useEffect(() => {
    if (onRefreshRef) {
      onRefreshRef(loadJobs)
    }
  }, [onRefreshRef, loadJobs])

  // Expose handleAddJob to parent
  useEffect(() => {
    if (onAddJobRef) {
      onAddJobRef(handleAddJob)
    }
  }, [onAddJobRef, handleAddJob])

  // Expose handleBulkDelete to parent
  useEffect(() => {
    if (onBulkDeleteRef) {
      onBulkDeleteRef(handleBulkDelete)
    }
  }, [onBulkDeleteRef, handleBulkDelete])

  // Expose handleBulkAddToJobs to parent
  useEffect(() => {
    if (onBulkAddToJobsRef) {
      onBulkAddToJobsRef(handleBulkAddToJobs)
    }
  }, [onBulkAddToJobsRef, handleBulkAddToJobs])

  // Notify parent about selected count change
  useEffect(() => {
    if (onSelectedCountChange) {
      onSelectedCountChange(selectedRows.size)
    }
  }, [selectedRows, onSelectedCountChange])

  // Подсчет статусов
  useEffect(() => {
    const counts: Record<string, number> = {
      Bookmarked: 0,
      Applying: 0,
      Applied: 0,
      Interviewing: 0,
      Negotiating: 0,
      Accepted: 0,
      'I Withdrew': 0,
      'Not Selected': 0,
      'No Response': 0,
      Archived: 0,
    }

    jobs.forEach((job) => {
      counts[job.status] = (counts[job.status] || 0) + 1
    })

    onStatusCountsChange(counts)
  }, [jobs, onStatusCountsChange])
}
